import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Trash2, Edit2, ListPlus, Wand2, Sparkles, 
  Loader2, ImageOff, MoreVertical, Share2, Tag, Plus,
  Check, X
} from 'lucide-react';
import { useRecipeStore } from '../store/recipeStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { GrocerySearch } from '../components/GrocerySearch';
import { INGREDIENT_CATEGORIES } from '../types/recipe';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { ShareRecipeModal } from '../components/ShareRecipeModal';
import { supabase } from '../lib/supabase';
import axios from 'axios';

export function RecipeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { recipes, deleteRecipe, updateRecipe } = useRecipeStore();
  const { isPremium } = useSubscriptionStore();
  const [enhancing, setEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAddTagInput, setShowAddTagInput] = useState(false);
  const [newTag, setNewTag] = useState('');

  const recipe = recipes.find(r => r.id === id);

  if (!recipe) {
    return (
      <div className="text-center py-8 text-gray-500">
        Recipe not found
      </div>
    );
  }

  const handleEnhance = async () => {
    setEnhancing(true);
    setEnhanceError(null);
    setShowMenu(false);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data: enhancedRecipe } = await axios.post(
        '/api/enhance-recipe',
        { recipe },
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      await updateRecipe(enhancedRecipe);
    } catch (err) {
      console.error('Failed to enhance recipe:', err);
      setEnhanceError(
        err instanceof Error 
          ? err.message 
          : axios.isAxiosError(err) && err.response?.data?.error
            ? err.response.data.error
            : 'Failed to enhance recipe'
      );
    } finally {
      setEnhancing(false);
    }
  };

  const handleDelete = async () => {
    await deleteRecipe(recipe.id);
    navigate('/recipes');
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    
    const updatedRecipe = {
      ...recipe,
      tags: [...(recipe.tags || []), newTag.trim()]
    };
    
    await updateRecipe(updatedRecipe);
    setNewTag('');
    setShowAddTagInput(false);
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const updatedRecipe = {
      ...recipe,
      tags: (recipe.tags || []).filter(tag => tag !== tagToRemove)
    };
    
    await updateRecipe(updatedRecipe);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/recipes')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-semibold flex-1 line-clamp-1">{recipe.title}</h1>

            {/* Share Button */}
            <button
              onClick={() => setShowShareModal(true)}
              className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Share Recipe"
            >
              <Share2 size={24} />
            </button>

            {/* Menu Button */}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <MoreVertical size={24} />
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                  <button
                    onClick={handleEnhance}
                    disabled={enhancing || !isPremium}
                    className="w-full flex items-center gap-2 px-4 py-2 text-purple-600 hover:bg-purple-50 disabled:opacity-50"
                  >
                    {enhancing ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <Wand2 size={20} />
                    )}
                    Enhance Recipe
                  </button>
                  <button
                    className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
                  >
                    <ListPlus size={20} />
                    Add to Meal
                  </button>
                  <button
                    className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
                  >
                    <Edit2 size={20} />
                    Edit Recipe
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={20} />
                    Delete Recipe
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {recipe.tags?.map((tag, index) => (
              <span 
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full group"
              >
                <Tag size={14} />
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-1 p-1 rounded-full hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            {showAddTagInput ? (
              <div className="inline-flex items-center gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add tag..."
                  className="px-2 py-1 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                />
                <button
                  onClick={handleAddTag}
                  className="p-1 text-green-600 hover:bg-green-50 rounded-full"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={() => setShowAddTagInput(false)}
                  className="p-1 text-gray-600 hover:bg-gray-100 rounded-full"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddTagInput(true)}
                className="inline-flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 text-sm rounded-full"
              >
                <Plus size={14} />
                Add Tag
              </button>
            )}
          </div>

          {enhanceError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {enhanceError}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {recipe.image_url && (
            <div className="mb-8">
              <img
                src={recipe.image_url}
                alt={recipe.title}
                className="w-full max-h-[400px] object-cover rounded-lg"
                onError={(e) => {
                  e.currentTarget.src = '';
                  e.currentTarget.classList.add('hidden');
                }}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-lg font-semibold mb-4">Ingredients</h2>
              <div className="space-y-6">
                {Object.entries(INGREDIENT_CATEGORIES)
                  .sort(([,a], [,b]) => a.priority - b.priority)
                  .map(([category, { name }]) => {
                    const categoryIngredients = recipe.ingredients.filter(
                      ing => ing.category === category
                    );
                    if (categoryIngredients.length === 0) return null;
                    
                    return (
                      <div key={category}>
                        <h3 className="font-medium text-gray-900 mb-2">{name}</h3>
                        <ul className="space-y-2">
                          {categoryIngredients.map((ing, idx) => (
                            <li key={idx} className="text-gray-700">
                              {ing.name}
                              {ing.alternatives.length > 0 && (
                                <div className="text-sm text-gray-500 ml-4">
                                  Alternatives: {ing.alternatives.join(', ')}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold mb-4">Instructions</h2>
              <ol className="list-decimal list-inside space-y-4">
                {recipe.instructions.map((step, idx) => (
                  <li key={idx} className="text-gray-700">{step}</li>
                ))}
              </ol>

              {recipe.notes && (
                <div className="mt-8">
                  <h2 className="text-lg font-semibold mb-4">Notes</h2>
                  <div className="text-gray-700 whitespace-pre-wrap">
                    {recipe.notes}
                  </div>
                </div>
              )}

              <div className="mt-8">
                <a
                  href={recipe.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  View Original Recipe
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <GrocerySearch ingredients={recipe.ingredients} />
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDeleteConfirm && (
        <ConfirmationModal
          title="Delete Recipe"
          message="Are you sure you want to delete this recipe? This action cannot be undone."
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          isDestructive
        />
      )}

      {showShareModal && (
        <ShareRecipeModal
          recipeUrl={recipe.source_url}
          recipeImage={recipe.image_url}
          recipeId={recipe.id}
          recipeTitle={recipe.title}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}